import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ButtonControl,
  SidePanelFormControl,
  TableControl,
} from 'spark-core-dx/components';
import { useCorePermissions, useGenericManager } from 'spark-core-dx/hooks';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import ResourceStateControl from '../resource/components/ResourceStateControl';
import {
  getFirstMetadata,
  getFirstResultItem,
  getResultMessage,
  isSuccessfulResult,
} from './clientEntryContracts';
import {
  CLIENT_CONFIGURATION_ITEM_NAMES,
  CLIENT_CONFIGURATION_OPERATIONS,
  CLIENT_FILE_FORM_FIELDS,
  CLIENT_FILE_STORAGE_FORM_FIELDS,
  selectFields,
} from './clientConfigurationContracts';
import { CLIENT_COMMAND_OPERATION_PERMISSIONS } from './clientCommandPermissions';

const runOperation = async (
  manager,
  operationName,
  item,
  fallbackMessage,
) => {
  if (!manager?.RunOperation) throw new Error(fallbackMessage);
  const result = await manager.RunOperation({
    operationName,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: false,
  });
  if (!isSuccessfulResult(result)) {
    throw new Error(getResultMessage(result, fallbackMessage));
  }
  return getFirstResultItem(result);
};

const ClientFilesState = ({ loading, error, message }) => {
  if (loading) {
    return (
      <ResourceStateControl
        compact
        loading
        title="Loading client files"
        loadingText="Loading the selected client's files."
      />
    );
  }
  if (error) {
    return (
      <ResourceStateControl
        compact
        tone="error"
        title="Client files unavailable"
        message={error}
      />
    );
  }
  return message ? (
    <div
      className="resource-workspace-access-note resource-workspace-access-note--success"
      role="status"
    >
      {message}
    </div>
  ) : null;
};

const fileId = (file) => file?.FileId || file?.UniqueId || file?._id || null;

const sanitizeDownloadFileNamePart = (value) => String(value || '')
  .trim()
  .replace(/\s/g, '_')
  .replace(/[<>:"/\\|?*]/g, '_')
  .replace(/\.+$/g, '');

const createDownloadFileName = (file) => {
  const displayName = sanitizeDownloadFileNamePart(file?.DisplayFileName);
  const extension = sanitizeDownloadFileNamePart(file?.FileExtension)
    .replace(/^\.+/g, '');
  if (!displayName || !extension ||
      displayName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
    return displayName;
  }
  return `${displayName}.${extension}`;
};

const clickDownloadLink = (url, fileName) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

const startFileDownload = async (
  url,
  fileName,
  requiresBlobFallback,
) => {
  if (!requiresBlobFallback) {
    clickDownloadLink(url, fileName);
    return;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('The client file bytes could not be downloaded.');
  }
  const objectUrl = URL.createObjectURL(await response.blob());
  try {
    clickDownloadLink(objectUrl, fileName);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const blankFileDraft = (clientId) => ({
  ClientId: clientId,
  StagedFileId: null,
  DisplayFileName: '',
  FileDescription: '',
  Tags: [],
  IsPublic: false,
  IsTemplate: false,
  ConcurrencyToken: '',
});

const fileRequest = ({
  clientId,
  currentEnvironment,
  workspace,
  fileDraft = null,
  selectedFileId = null,
  confirmReplacement = false,
  confirmRemoval = false,
}) => ({
  ClientId: clientId,
  CurrentEnvironment: currentEnvironment,
  SearchText: workspace?.SearchText || '',
  SortField: workspace?.SortField || 'FileUpdatedDate',
  SortDescending: workspace?.SortDescending !== false,
  PageSize: workspace?.PageSize || 100,
  PageNumber: workspace?.PageNumber || 1,
  ConfigurationVersion: workspace?.ConfigurationVersion || '',
  OperationCorrelationId: workspace?.OperationCorrelationId || '',
  FileDraft: fileDraft,
  SelectedFileId: selectedFileId,
  ConfirmReplacement: confirmReplacement,
  ConfirmRemoval: confirmRemoval,
});

const ClientStorageSummary = ({ workspace }) => {
  const usesClientStorage = workspace?.StorageMode === 'Client Azure Storage';
  const connectionStatus = workspace?.ConnectionStatus === 'Platform fallback'
    ? 'Platform Default'
    : workspace?.ConnectionStatus || 'Platform Default';
  const storageLocation = [
    workspace?.StorageAccountName,
    workspace?.StorageContainer,
  ].filter(Boolean).join(' / ') || 'Platform-managed';

  return (
    <section
      className="resource-workspace-panel"
      aria-label="Client file storage summary"
    >
      <div className="resource-workspace-section-title resource-workspace-section-title--embedded">
        <h2>Storage summary</h2>
        <p>
          {workspace?.LastValidationSummary
            || (usesClientStorage
              ? 'New client files use the selected client Azure Storage connection.'
              : 'New client files use current platform storage.')}
          {' '}Account keys and connection secrets are never displayed.
        </p>
      </div>
      <dl className="resource-workspace-facts command-files__storage">
        <div>
          <dt>New files</dt>
          <dd>{workspace?.StorageMode || 'Platform Storage'}</dd>
        </div>
        <div>
          <dt>Connection</dt>
          <dd>{connectionStatus}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>{storageLocation}</dd>
        </div>
      </dl>
    </section>
  );
};

const ClientFilesScreen = ({
  screen,
  clientId,
  currentEnvironment,
}) => {
  const { canExecute } = useCorePermissions();
  const workspaceMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.clientFilesWorkspace,
  );
  const fileMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.clientFile,
  );
  const storageMgr = useGenericManager(
    CLIENT_CONFIGURATION_ITEM_NAMES.clientFileStorageSetup,
  );
  const workspaceMgrRef = useRef(workspaceMgr);
  const fileMgrRef = useRef(fileMgr);
  const storageMgrRef = useRef(storageMgr);
  const [workspace, setWorkspace] = useState(null);
  const [fileMetadata, setFileMetadata] = useState(null);
  const [storageMetadata, setStorageMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [filePanelOpen, setFilePanelOpen] = useState(false);
  const [fileDraft, setFileDraft] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [storagePanelOpen, setStoragePanelOpen] = useState(false);
  const [storageDraft, setStorageDraft] = useState(null);
  const [pendingReplacement, setPendingReplacement] = useState(null);
  const [confirmRevert, setConfirmRevert] = useState(false);
  workspaceMgrRef.current = workspaceMgr;
  fileMgrRef.current = fileMgr;
  storageMgrRef.current = storageMgr;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const [loaded, fileMetadataResult, storageMetadataResult] =
        await Promise.all([
          runOperation(
            workspaceMgrRef.current,
            CLIENT_CONFIGURATION_OPERATIONS.loadClientFiles,
            {
              ClientId: clientId,
              CurrentEnvironment: currentEnvironment,
            },
            'Client files are unavailable.',
          ),
          fileMgrRef.current.GetMetadata({ forceReload: true }),
          storageMgrRef.current.GetMetadata({ forceReload: true }),
        ]);
      setWorkspace(loaded);
      setFileMetadata(
        getFirstMetadata(fileMetadataResult, fileMgrRef.current),
      );
      setStorageMetadata(
        getFirstMetadata(storageMetadataResult, storageMgrRef.current),
      );
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client files are unavailable.');
    } finally {
      setLoading(false);
    }
  }, [clientId, currentEnvironment]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const canSaveFile = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientFile,
  );
  const canRemoveFile = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.removeClientFile,
  );
  const canDownloadFile = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.resolveClientFileLocation,
  );
  const canSetUpStorage = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.setUpClientFileStorage,
  );
  const canValidateStorage = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.validateClientFileStorage,
  );
  const canRevertStorage = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.revertClientFileStorageToPlatform,
  );
  const fileFields = useMemo(
    () => selectFields(fileMetadata, CLIENT_FILE_FORM_FIELDS).map((field) => {
      const name = String(field?.FieldName || field?.fieldName || '');
      if (name.toLowerCase() === 'filedescription') {
        return {
          ...field,
          IsRequired: false,
        };
      }
      if (name.toLowerCase() === 'ispublic' && selectedFile) {
        return {
          ...field,
          IsReadOnly: true,
          Disabled: true,
        };
      }
      return field;
    }),
    [fileMetadata, selectedFile],
  );
  const storageFields = useMemo(
    () => selectFields(storageMetadata, CLIENT_FILE_STORAGE_FORM_FIELDS),
    [storageMetadata],
  );
  const fileGridMetadata = useMemo(
    () => (fileMetadata ? {
      ...fileMetadata,
      DisplayName: '',
      Description: '',
      DisableCanEdit: !canSaveFile,
      DisableCanDelete: !canRemoveFile,
    } : null),
    [canRemoveFile, canSaveFile, fileMetadata],
  );
  const pageCount = Math.max(
    1,
    Math.ceil((workspace?.TotalFiles || 0) / (workspace?.PageSize || 100)),
  );

  const loadFilesPage = async (pageNumber = 1) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const loaded = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.searchClientFiles,
        {
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          SearchText: workspace?.SearchText || '',
          SortField: workspace?.SortField || 'FileUpdatedDate',
          SortDescending: workspace?.SortDescending !== false,
          PageSize: workspace?.PageSize || 100,
          PageNumber: pageNumber,
        },
        'Client file search is unavailable.',
      );
      setWorkspace(loaded);
    } catch (ex) {
      setError(ex?.message || 'Client file search is unavailable.');
    } finally {
      setBusy(false);
    }
  };

  const openNewFile = () => {
    setSelectedFile(null);
    setFileDraft(blankFileDraft(clientId));
    setFilePanelOpen(true);
  };

  const openEditFile = (file) => {
    setSelectedFile(file);
    setFileDraft({
      ...file,
      ClientId: clientId,
      StagedFileId: null,
    });
    setFilePanelOpen(true);
  };

  const executeFileSave = async (
    updated,
    target = selectedFile,
    confirmReplacement = false,
  ) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const normalized = {
        ...updated,
        ClientId: clientId,
        StagedFileId: updated?.StagedFileId || null,
        ConcurrencyToken: target?.ConcurrencyToken || updated?.ConcurrencyToken || '',
        IsPublic: target ? target.IsPublic : !!updated?.IsPublic,
      };
      const saved = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.saveClientFile,
        fileRequest({
          clientId,
          currentEnvironment,
          workspace,
          fileDraft: normalized,
          selectedFileId: fileId(target),
          confirmReplacement,
        }),
        'The client file was not saved.',
      );
      setWorkspace(saved);
      setFilePanelOpen(false);
      setFileDraft(null);
      setSelectedFile(null);
      setPendingReplacement(null);
      setMessage(target
        ? 'The client file was updated.'
        : 'The client file was uploaded.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message || 'The client file was not saved.',
      };
    } finally {
      setBusy(false);
    }
  };

  const saveFile = async (updated) => {
    if (selectedFile && updated?.StagedFileId) {
      setPendingReplacement({
        file: selectedFile,
        draft: updated,
      });
      setFilePanelOpen(false);
      return { Success: true, Items: [updated] };
    }
    return executeFileSave(updated);
  };

  const downloadFile = async (file) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.resolveClientFileLocation,
        fileRequest({
          clientId,
          currentEnvironment,
          workspace,
          selectedFileId: fileId(file),
        }),
        'The client file download is unavailable.',
      );
      setWorkspace(result);
      if (!result?.DownloadUrl) {
        throw new Error('The client file download URL is unavailable.');
      }
      const serverDownloadFileName =
        sanitizeDownloadFileNamePart(result?.DownloadFileName);
      const resolvedDownloadFileName =
        serverDownloadFileName || createDownloadFileName(file);
      if (!resolvedDownloadFileName) {
        throw new Error('The client file download name is unavailable.');
      }
      await startFileDownload(
        result.DownloadUrl,
        resolvedDownloadFileName,
        !serverDownloadFileName,
      );
      setMessage('The selected client file download was started.');
    } catch (ex) {
      setError(ex?.message || 'The client file download is unavailable.');
    } finally {
      setBusy(false);
    }
  };

  const removeFile = async (file) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.removeClientFile,
        fileRequest({
          clientId,
          currentEnvironment,
          workspace,
          selectedFileId: fileId(file),
          confirmRemoval: true,
        }),
        'The client file was not removed.',
      );
      setWorkspace(saved);
      setMessage('The selected client file was removed.');
      return saved;
    } catch (ex) {
      setError(ex?.message || 'The client file was not removed.');
      throw ex;
    } finally {
      setBusy(false);
    }
  };

  const openStorage = () => {
    setStorageDraft({
      ...(workspace?.StorageSetup || {}),
      AccountKeyInput: '',
      ExpectedConfigurationVersion: workspace?.ConfigurationVersion || '',
    });
    setStoragePanelOpen(true);
  };

  const saveStorage = async (updated) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        CLIENT_CONFIGURATION_OPERATIONS.setUpClientFileStorage,
        {
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          StorageSetup: {
            ...updated,
            AccountKeyInput: updated?.AccountKeyInput || '',
            ExpectedConfigurationVersion:
              workspace?.ConfigurationVersion || '',
          },
        },
        'Client Azure file storage was not configured.',
      );
      setWorkspace(saved);
      setStoragePanelOpen(false);
      setStorageDraft(null);
      setMessage('Client Azure file storage is active for new files.');
      return { Success: true, Items: [saved] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage:
          ex?.message || 'Client Azure file storage was not configured.',
      };
    } finally {
      setBusy(false);
    }
  };

  const executeStorageOperation = async (
    operationName,
    successMessage,
    errorSurface = 'page',
  ) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await runOperation(
        workspaceMgrRef.current,
        operationName,
        {
          ClientId: clientId,
          CurrentEnvironment: currentEnvironment,
          ConfigurationVersion: workspace?.ConfigurationVersion || '',
        },
        successMessage,
      );
      setWorkspace(saved);
      setConfirmRevert(false);
      setMessage(successMessage);
      return { Success: true, Items: [saved] };
    } catch (ex) {
      const failureMessage = ex?.message || successMessage;
      if (errorSurface === 'page') {
        setError(failureMessage);
      }
      return {
        Success: false,
        DisplayMessage: failureMessage,
      };
    } finally {
      setBusy(false);
    }
  };

  const rowActions = () => {
    return canDownloadFile ? [
      { name: 'Download', customFunction: downloadFile },
    ] : [];
  };
  const commandBarActions = [
    {
      key: 'refresh-client-files',
      label: 'Refresh',
      iconKey: 'refresh',
      disabled: loading || busy,
      onClick: refresh,
    },
    ...(canSaveFile ? [
      {
        key: 'upload-client-file',
        label: 'Upload File',
        iconKey: 'add',
        disabled: !workspace || !fileMetadata || busy,
        onClick: openNewFile,
      },
    ] : []),
    ...(canSetUpStorage ? [{
        key: 'configure-client-storage',
        label: 'Set Up Client Storage',
        iconKey: 'settings',
        disabled: !workspace || !storageMetadata || busy,
        onClick: openStorage,
      }] : []),
    ...(canValidateStorage ? [{
        key: 'validate-client-storage',
        label: 'Validate Storage',
        iconKey: 'validate',
        disabled: workspace?.StorageMode !== 'Client Azure Storage' || busy,
        onClick: () => executeStorageOperation(
          CLIENT_CONFIGURATION_OPERATIONS.validateClientFileStorage,
          'Client Azure file-storage access is valid.',
        ),
      }] : []),
    ...(canRevertStorage ? [{
        key: 'revert-client-storage',
        label: 'Use Platform Storage',
        iconKey: 'undo',
        disabled: workspace?.StorageMode !== 'Client Azure Storage' || busy,
        onClick: () => setConfirmRevert(true),
      }] : []),
  ];

  return (
    <div className="resource-workspace-overview command-entry command-files">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Client Files actions"
        className="resource-workspace-configuration-command-bar"
        actionClassName="resource-workspace-command-bar-action"
        actionRightClassName="resource-workspace-command-bar-action--right"
        actionDangerClassName="resource-workspace-command-bar-action--danger"
        actionSeparatorAfterClassName="resource-workspace-command-bar-action--separator-after"
        overflowWrapperClassName="resource-workspace-command-bar-overflow"
        measureClassName="resource-workspace-command-bar-measure"
        menuClassName="resource-workspace-command-bar-menu"
        menuActionDangerClassName="resource-workspace-command-bar-menu-action--danger"
        loadingClassName="resource-workspace-command-bar-action__loading"
      />

      <ClientFilesState loading={loading} error={error} message={message} />
      {!loading && workspace ? (
        <>
          <ClientStorageSummary workspace={workspace} />
          {fileGridMetadata ? (
            <div className="command-files__grid">
              <TableControl
                data={workspace.Files || []}
                defaultNumRows={workspace.PageSize || 100}
                disableTableTitle
                headerFeatures={[
                  'search',
                  'filter',
                  'tableStyles',
                  'columnSettings',
                ]}
                enableTableDataSettings={false}
                enableMultiSelect={false}
                filters={fileMgr?.filters}
                loadingState={busy}
                metadata={[fileGridMetadata]}
                mgr={fileMgr}
                onDelete={canRemoveFile ? removeFile : undefined}
                onEdit={canSaveFile ? openEditFile : undefined}
                additionalQuickViewList={rowActions}
              />
            </div>
          ) : null}
          <div className="command-files__paging">
            <span>
              Page {workspace.PageNumber || 1} of {pageCount}
            </span>
            <div className="command-entry__actions">
              <ButtonControl
                type="standard-small"
                onClick={() => loadFilesPage((workspace.PageNumber || 1) - 1)}
                disabled={busy || (workspace.PageNumber || 1) <= 1}
              >
                Previous
              </ButtonControl>
              <ButtonControl
                type="standard-small"
                onClick={() => loadFilesPage((workspace.PageNumber || 1) + 1)}
                disabled={busy || (workspace.PageNumber || 1) >= pageCount}
              >
                Next
              </ButtonControl>
            </div>
          </div>
        </>
      ) : null}

      <SidePanelFormControl
        open={filePanelOpen}
        title={selectedFile ? 'Edit Client File' : 'Upload Client File'}
        metadata={fileMetadata}
        formFields={fileFields}
        itemData={fileDraft}
        onSave={saveFile}
        onCancel={() => setFilePanelOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Saving...' : 'Save'}
        setOpenEditSidePanel={setFilePanelOpen}
        setItemData={setFileDraft}
        viewOnly={false}
      />

      <SidePanelFormControl
        open={storagePanelOpen}
        title="Set Up Client Storage"
        metadata={storageMetadata}
        formFields={storageFields}
        itemData={storageDraft}
        onSave={saveStorage}
        onCancel={() => setStoragePanelOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={busy ? 'Validating...' : 'Validate and Save'}
        setOpenEditSidePanel={setStoragePanelOpen}
        setItemData={setStorageDraft}
        viewOnly={false}
      />

      <DialogControl
        openDialog={!!pendingReplacement}
        title="Replace stored file bytes"
        subTitle="This is not recommended. It overwrites the same FileItem content while preserving its identity, client, storage location, and references."
        okText="Confirm Replacement"
        cancelText="Cancel"
        onCancel={() => setPendingReplacement(null)}
        onX={() => setPendingReplacement(null)}
        onOk={() => executeFileSave(
          pendingReplacement.draft,
          pendingReplacement.file,
          true,
        )}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          Replace the stored bytes for
          {' '}{pendingReplacement?.file?.DisplayFileName || 'this file'}?
          The extension, MIME type, visibility, client, and reference safety
          checks will run again on the server.
        </p>
      </DialogControl>

      <DialogControl
        openDialog={confirmRevert}
        title="Use platform storage for new files"
        subTitle="Historical protected connection versions are retained so existing files remain readable and replaceable at their recorded locations."
        okText="Confirm Platform Storage"
        cancelText="Cancel"
        onCancel={() => setConfirmRevert(false)}
        onX={() => setConfirmRevert(false)}
        onOk={() => executeStorageOperation(
          CLIENT_CONFIGURATION_OPERATIONS.revertClientFileStorageToPlatform,
          'New client files now use platform storage.',
          'dialog',
        )}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          Revert only new file writes for this client to the current platform
          storage?
        </p>
      </DialogControl>
    </div>
  );
};

export default ClientFilesScreen;
